// src/utils/permissionTranslator.ts
import { TFunction } from 'i18next';
import { splitPermission } from '../constants/permissions';

/**
 * Human label for a permission name such as `"sell.create"`.
 *
 * Prefers an explicit `permissions.<name>` translation. The system carries far
 * more permissions than there are translations for, so the fallback is the
 * readable action ("Create", "Reset stock") rather than the raw dotted name —
 * the group it belongs to is already shown as the heading above it.
 */
export function translatePermission(permission: string, t: TFunction): string {
    const translation = t(`permissions.${permission}`);
    if (!translation.startsWith('permissions.')) {
        return translation;
    }

    const { action } = splitPermission(permission);
    if (!action) return permission;

    // `recalculateStock` -> `Recalculate stock`, `Order.shipped` -> `Order shipped`
    const words = action
        .replace(/\./g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .toLowerCase();

    return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Full label including the group, for places with no grouping heading. */
export function translatePermissionWithGroup(permission: string, t: TFunction): string {
    const { group } = splitPermission(permission);
    const groupLabel = t(`permission_group.${group}`, { defaultValue: group });
    return `${groupLabel} · ${translatePermission(permission, t)}`;
}
